import { memo, useCallback, useMemo } from 'react'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { UnitSelectField } from './UnitSelectField'
import { INPUTS, COMPOUND_INPUT, TYPOGRAPHY } from './styles'
import { cn } from '@shared/lib/utils'

interface BaseQuantityInputProps {
  label: string
  labelWidth: 'narrow' | 'wide'
  quantity: string
  unit: string
  showCustomUnitInput: boolean
  customUnit: string
  onQuantityChange: (quantity: string) => void
  onUnitChange: (unit: string) => void
  onCustomUnitChange: (value: string) => void
  onUnitDropdownClose: (open: boolean) => void
  placeholder?: string
  ariaLabel?: string
  fieldLabel?: string
  description?: string
  className?: string
}

export const BaseQuantityInput = memo<BaseQuantityInputProps>(function BaseQuantityInput({
  label,
  labelWidth,
  quantity,
  unit,
  showCustomUnitInput,
  customUnit,
  onQuantityChange,
  onUnitChange,
  onCustomUnitChange,
  onUnitDropdownClose,
  placeholder = "10",
  ariaLabel,
  fieldLabel,
  description,
  className,
}) {
  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onQuantityChange(e.target.value)
  }, [onQuantityChange])

  const labelWidthClass = labelWidth === 'narrow'
    ? COMPOUND_INPUT.labelSectionNarrow
    : COMPOUND_INPUT.labelSectionWide

  const summaryText = useMemo(() => {
    const num = parseInt(quantity) || 0
    const displayUnit = showCustomUnitInput ? (customUnit || 'units') : (unit || 'units')
    if (num === 0) return null
    if (num === 1) {
      // Only strip trailing 's' for known plural form "units"
      const singular = displayUnit === 'units' ? 'unit' : displayUnit
      return `${num} ${singular}`
    }
    // For plural: add 's' only if the unit doesn't already end in 's'
    const plural = displayUnit.endsWith('s') ? displayUnit : `${displayUnit}s`
    return `${num} ${plural}`
  }, [quantity, unit, showCustomUnitInput, customUnit])

  return (
    <div className={cn('space-y-1.5', className)}>
      {fieldLabel && <Label className={TYPOGRAPHY.label}>{fieldLabel}</Label>}
      <div className={COMPOUND_INPUT.wrapper} role="group" aria-label={ariaLabel}>
        <div className={cn(COMPOUND_INPUT.labelSectionBase, labelWidthClass)}>
          <span className="text-sm text-muted-foreground opacity-60 whitespace-nowrap">
            {label}
          </span>
        </div>
        <div className="flex-1 relative">
          <Input
            type="number"
            placeholder={placeholder}
            value={quantity}
            onChange={handleQuantityChange}
            className={cn(INPUTS.quantity, COMPOUND_INPUT.inputSection)}
            aria-label={`Quantity ${label}`}
          />
        </div>
        <UnitSelectField
          value={unit}
          showCustomInput={showCustomUnitInput}
          customValue={customUnit}
          onUnitChange={onUnitChange}
          onCustomValueChange={onCustomUnitChange}
          onOpenChange={onUnitDropdownClose}
          className={COMPOUND_INPUT.selectSection}
        />
      </div>
      {description ? (
        <p className="text-xs text-muted-foreground font-light leading-tight">{description}</p>
      ) : summaryText ? (
        <p className="text-xs text-muted-foreground/60 font-light leading-tight">{summaryText}</p>
      ) : null}
    </div>
  )
})

BaseQuantityInput.displayName = 'BaseQuantityInput'
